import logoPrefeitura from "@/assets/logo-da-prefeitura-750x375.png";
import logoSLZ from "@/assets/logo-dark.png";
import logoHospital from "@/assets/logo-hospital-372x2550.png";
import { useAuth } from "@/context/AuthProvider";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { LoginService } from "@/services/auth/login";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const loginSchema = z.object({
  documento: z
    .string()
    .min(11, "Documento deve ter no mínimo 11 dígitos")
    .max(14, "Documento deve ter no máximo 14 dígitos")
    .regex(/^\d+$/, "Documento deve conter apenas números")
    .refine((value) => {
      return value.length === 11 || value.length === 14;
    }, "Documento deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ)"),
  senha: z.string().min(6, "Senha deve ter pelo menos 6 caracteres"),
});

type LoginData = z.infer<typeof loginSchema>;

export function Login() {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { login } = useAuth();
  const authService = new LoginService();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      documento: "",
      senha: "",
    },
  });

  const loginMutation = useMutation<
    {
      token: string;
      user: {
        id: number;
        nome: string;
        documento: string;
        email: string;
        type: "tutor" | "ong";
        publicId: string;
        isEmailVerified: boolean;
      };
    },
    Error,
    LoginData
  >({
    mutationFn: async (credentials: LoginData) => {
      const response = await authService.login({
        ...credentials,
      });
      return response;
    },
    onSuccess: async (data) => {
      localStorage.setItem("vet:token", String(data.token));
      api.defaults.headers["Authorization"] = `Bearer ${data.token}`;
      login(data.user);
    },
    onError: (error: Error) => {
      setLoginError(error.message || "Falha ao fazer login. Tente novamente.");
    },
  });

  const onSubmit = (data: LoginData) => {
    setLoginError(null);
    loginMutation.mutate(data);
  };

  const handleCadastro = () => {
    navigate("/sign-in");
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div className="bg-white h-svh overflow-hidden flex flex-col justify-between px-5">
      {/* Top Content */}
      <div className="flex-1 flex flex-col justify-center items-center min-h-0">
        {/* Logo Section */}
        <div className="flex-shrink-0 text-center mb-8">
          <div className="w-48 h-24 mx-auto">
            <img
              src={logoHospital}
              className="w-full h-full object-contain"
              alt="Logo Hospital"
            />
          </div>
        </div>

        {/* Form Section */}
        <div className="w-full max-w-7xl flex-shrink-0 mt-5">
            <div className="bg-white rounded-xl px-2">
              <div className="mb-4">
                <h1 className="text-xl font-bold text-primary mb-3">
                  Acesse sua conta
                </h1>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">
                    CPF ou CNPJ
                  </label>
                  <Controller
                    control={control}
                    name="documento"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div className="relative mb-1">
                        <input
                          type="text"
                          inputMode="numeric"
                          className={cn(
                            "w-full bg-white rounded-full h-12 px-4 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm",
                            errors.documento
                              ? "border-red-500"
                              : "border-gray-200"
                          )}
                          placeholder="Digite seu CPF ou CNPJ"
                          maxLength={14}
                          onBlur={onBlur}
                          value={value}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            onChange(digits);
                          }}
                          disabled={loginMutation.isPending}
                        />
                      </div>
                    )}
                  />
                  {errors.documento && (
                    <p className="text-red-500 text-xs mb-1">
                      {errors.documento.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">
                    Senha
                  </label>
                  <Controller
                    control={control}
                    name="senha"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div className="relative mb-1">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          className={cn(
                            "w-full bg-white rounded-full h-12 px-4 border placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-16 text-sm",
                            errors.senha
                              ? "border-red-500"
                              : "border-gray-200"
                          )}
                          placeholder="Digite sua senha"
                          onBlur={onBlur}
                          autoCapitalize="none"
                          autoCorrect={'off'}
                          onChange={(e) => onChange(e.target.value)}
                          value={value}
                          disabled={loginMutation.isPending}
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-3 top-3 text-primary text-xs hover:underline focus:outline-none"
                          disabled={loginMutation.isPending}
                        >
                          {passwordVisible ? "Ocultar" : "Mostrar"}
                        </button>
                      </div>
                    )}
                  />
                  {errors.senha && (
                    <p className="text-red-500 text-xs mb-1">
                      {errors.senha.message}
                    </p>
                  )}

                  <button
                    type="button"
                    className="block ml-auto mb-2 text-primary text-xs hover:underline focus:outline-none"
                    disabled={loginMutation.isPending}
                    onClick={handleForgotPassword}
                  >
                    Esqueceu a senha?
                  </button>
                </div>

                {loginError && (
                  <div className="mb-3">
                    <p className="text-red-500 text-sm text-center">
                      {loginError}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  className={cn(
                    "w-full bg-primary rounded-full h-12 flex items-center justify-center text-white font-bold hover:bg-[#1562a3] transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm",
                    loginMutation.isPending && "opacity-70 cursor-not-allowed"
                  )}
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Entrando...
                    </div>
                  ) : (
                    "Entrar"
                  )}
                </button>

                <div className="flex items-center my-3">
                  <div className="flex-1 h-px bg-gray-300" />
                  <span className="mx-2 text-gray-500 text-xs">ou</span>
                  <div className="flex-1 h-px bg-gray-300" />
                </div>

                <button
                  type="button"
                  className={cn(
                    "w-full border border-primary rounded-full h-12 flex items-center justify-center text-primary font-bold text-sm hover:bg-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                    loginMutation.isPending && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={handleCadastro}
                  disabled={loginMutation.isPending}
                >
                  Cadastrar
                </button>
              </form>
        </div>
      </div>
      
      {/* Footer Logos - Fixed at bottom */}
      <div className="flex-shrink-0 flex items-center justify-center pb-6 pt-4 gap-2">
        <img
          src={logoPrefeitura}
          className="h-14 w-32 object-contain"
          alt="Logo Prefeitura"
        />
        <img
          src={logoSLZ}
          className="h-14 w-24 object-contain"
          alt="Logo SLZ"
        />
      </div>
    </div>
    </div>
  );
}